import api from '../helpers/api';
import { select, all, takeEvery, call, put, take } from 'redux-saga/effects';

import * as bucketSelectors from '../selectors/bucketSelectors';
import * as organizationSelectors from '../selectors/organizationSelectors';
import * as reportSelectors from '../selectors/reportSelectors';

import * as organizationConstants from '../constants/organizations';
import { getErrorMessage } from '../helpers/Util';

import downloadFile from '../helpers/DownloadFile';
import BackendPaths from '../helpers/BackendPaths';

function getUTCDate(date) {
    const [year, month, day] = date.split('-');
    const utcDate = new Date();
    utcDate.setUTCFullYear(+year);
    utcDate.setUTCMonth(+month - 1);
    utcDate.setUTCDate(+day);
    return utcDate;
}
function getUTCStartOfDay(date) {
    const utcDate = getUTCDate(date);
    utcDate.setUTCHours(0);
    utcDate.setUTCMinutes(0);
    utcDate.setUTCSeconds(0);
    // no need to set milliseconds, since backed works with seconds anyway
    return Math.trunc(utcDate.getTime() / 1000);
}
function getUTCEndOfDay(date) {
    const utcDate = getUTCDate(date);
    utcDate.setUTCHours(23);
    utcDate.setUTCMinutes(59);
    utcDate.setUTCSeconds(59);
    // no need to set milliseconds, since backed works with seconds anyway
    return Math.trunc(utcDate.getTime() / 1000);
}

function* report(action) {
    const { start, end, organizationId } = action.payload;
    const isLoadingOrganizations = yield select(
        organizationSelectors.getIsLoadingOrganizations,
    );
    if (isLoadingOrganizations) {
        yield take(organizationConstants.FETCH_ORGANIZATIONS_SUCCESS);
    }
    const organization = yield select(
        organizationSelectors.getOrganization,
        organizationId,
    );
    const isLoadingBuckets = yield select(bucketSelectors.getIsLoadingBuckets);
    if (isLoadingBuckets === undefined || isLoadingBuckets) {
        yield take(organizationConstants.FETCH_ORGANIZATION_BUCKETS_SUCCESS);
    }
    const organizationBuckets = yield select(
        bucketSelectors.getBuckets,
        organizationId,
    );

    // send list of all projects that are in selected organization
    const search = new URLSearchParams();

    // set which organization, indicating to count all projects that belong to it
    search.append('organizationId', organizationId);
    // set time interval for which report has to be generated
    search.append('firstPublishedBetween[]', getUTCStartOfDay(start));
    search.append('firstPublishedBetween[]', getUTCEndOfDay(end));
    // ask backend to group counts by owner Id
    search.append('groupByOwner', true);

    const queryString = '?' + search.toString();
    let responses;
    try {
        const applications = organization.visibleInApplications.filter(
            app => Object.keys(BackendPaths.reportCount).indexOf(app.id) >= 0,
        );

        // send calls for all search endpoints in parallel
        // For each application one call is made (charts and CEB at most when
        // this comment has been written)
        if (organizationBuckets.length > 0) {
            responses = yield all(
                applications.map(app =>
                    call(
                        api,
                        BackendPaths.reportCount[app.id].getPath() +
                            queryString,
                    ),
                ),
            );
        } else {
            // Construct empty responses if there are no buckets
            responses = applications.map(() => ({ count: [] }));
        }
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: organizationConstants.REPORT_FAILURE,
            payload: getErrorMessage(response),
        });
        return;
    }

    //////////////////////////////////////////////
    // Preprocessing data retrieved from server //
    //////////////////////////////////////////////
    // 1. Store data retrieved from server in hash.
    // Hash key is identity id, value is array of counts received from backend,
    // where each element of array is value for particular application.
    // {
    //     "12": [12, 4]
    //     "41": [0, 1]
    //     "1845": [5, 0]
    // }
    const dataInHash = {};
    responses.forEach((response, index) => {
        response.count.forEach(({ identityId, count }) => {
            if (!dataInHash[identityId]) {
                dataInHash[identityId] = Array(responses.length).fill(0);
            }
            dataInHash[identityId][index] = count;
        });
    });

    // 2. Convert hash to 2D array and sort by bucket name
    // +-----------+----+---+
    // | Project 1 | 12 | 4 |
    // | Project 2 |  0 | 1 |
    // | Project 3 |  5 | 0 |
    // +-----------+----+---+
    const data = Object.keys(dataInHash)
        .map(identityId => {
            const bucket = organizationBuckets.find(
                bucket => bucket.id === +identityId,
            );

            if (!bucket) {
                return undefined;
            }

            return [bucket.name, ...dataInHash[identityId]];
        })
        .filter(a => a !== undefined)
        .sort((a, b) => a[0].localeCompare(b[0]));

    // 3. Add total row at the end of data
    // +-----------------+----+---+
    // | Project 1       | 12 | 4 |
    // | Project 2       |  0 | 1 |
    // | Project 3       |  5 | 0 |
    // | Total 3 projects| 17 | 5 | <--- add this row
    // +-----------------+----+---+
    const total = Array(responses.length).fill(0);
    data.forEach(row => {
        row.slice(1).forEach((cell, index) => (total[index] += cell || 0));
    });
    // pluralize word project
    const project = data.length === 1 ? 'project' : 'projects';
    data.push([`Total ${data.length} ${project}`, ...total]);

    yield put({
        type: organizationConstants.REPORT_SUCCESS,
        payload: data,
    });
}

function* watchReport() {
    yield takeEvery(organizationConstants.REPORT_REQUEST, report);
}

function* downloadReport(action) {
    const { start, end, organizationId } = action.payload;
    const data = yield select(reportSelectors.getData);
    const header = yield select(reportSelectors.getHeader);
    const organization = yield select(
        organizationSelectors.getOrganization,
        organizationId,
    );

    const csvHeader = header.join(',');
    // per https://tools.ietf.org/html/rfc4180, 2.7., double quotes are escaped
    // using two double quotes
    const csvData = data
        .map(row => `"${row[0].replace(/"/g, '""')}",${row.slice(1).join(',')}`)
        .join('\r\n');
    const csvContent = csvHeader + '\r\n' + csvData;
    const organizationName =
        organization.name.replace(/[^\w-]/g, '') || organization.id.toString();
    const csvFileName = `${organizationName}_${start}-${end}.csv`;
    yield call(
        downloadFile,
        csvFileName,
        csvContent,
        'text/csv;encoding:utf-8',
    );
}

function* watchDownloadReport() {
    yield takeEvery(
        organizationConstants.DOWNLOAD_REPORT_REQUEST,
        downloadReport,
    );
}

export default function*() {
    yield all([watchReport(), watchDownloadReport()]);
}



// WEBPACK FOOTER //
// ./src/sagas/reportSaga.js